import { strictify } from '~/utils/strictify'
import { createGeneratedResourceOperation } from '~/services/api/createGeneratedResourceOperation'
import { PaymentMethodSave } from '~/services/api/contracts/requests/PaymentMethodSave'
import { PaymentOnboardingCreateChild } from '~/services/api/contracts/requests/PaymentOnboardingCreateChild'
import { PaymentOnboardingSession } from '~/services/api/contracts/requests/PaymentOnboardingSession'
import { PaymentOnboardingStore } from '~/services/api/contracts/requests/PaymentOnboardingStore'
import { z } from 'zod'
import { pdfBlobSchema } from '~/utils/pdfBlobSchema'
import type { Payout } from '~/services/api/interfaces/Payout'
import {
  PaymentMethodDeletePathSchema,
  PaymentMethodDeleteSuccessSchema,
  PaymentMethodListSuccessSchema,
  PaymentMethodSaveSuccessSchema,
  PaymentMethodSetDefaultPathSchema,
  PaymentMethodSetDefaultSuccessSchema,
  PaymentMethodSetupIntentSuccessSchema,
  PaymentOnboardingAllSuccessSchema,
  PaymentOnboardingAssignParentPathSchema,
  PaymentOnboardingAssignParentSuccessSchema,
  PaymentOnboardingChangeBusinessTypePathSchema,
  PaymentOnboardingChangeBusinessTypeRequestSchema,
  PaymentOnboardingChangeBusinessTypeSuccessSchema,
  PaymentOnboardingCompletedSuccessSchema,
  PaymentOnboardingCreateChildPathSchema,
  PaymentOnboardingCreateChildSuccessSchema,
  PaymentOnboardingDestroyPathSchema,
  PaymentOnboardingDestroySuccessSchema,
  PaymentOnboardingListSuccessSchema,
  PaymentOnboardingModelsWithoutMerchantPathSchema,
  PaymentOnboardingModelsWithoutMerchantSuccessSchema,
  PaymentOnboardingSessionPathSchema,
  PaymentOnboardingSessionSuccessSchema,
  PaymentOnboardingShowForPayableQuerySchema,
  PaymentOnboardingShowForPayableSuccessSchema,
  PaymentOnboardingShowPathSchema,
  PaymentOnboardingShowSuccessSchema,
  PaymentOnboardingStoreSuccessSchema,
  PaymentOnboardingSyncPathSchema,
  PaymentOnboardingSyncSuccessSchema,
  PaymentOnboardingWebsiteCandidatesQuerySchema,
  PaymentOnboardingWebsiteCandidatesSuccessSchema,
  PayoutsDownloadPathSchema,
  PayoutsForPartnerQuerySchema,
  PayoutsForPartnerSuccessSchema,
  PayoutsForStorePathSchema,
  PayoutsForStoreQuerySchema,
  PayoutsForStoreSuccessSchema,
  PayoutsShowPathSchema,
  PayoutsShowSuccessSchema
} from '~/services/api/generated'
import type {
  PaymentMethodSaveRequest,
  PaymentOnboardingChangeBusinessTypeRequest,
  PaymentOnboardingCreateChildRequest,
  PaymentOnboardingModelsWithoutMerchantPath,
  PaymentOnboardingSessionRequest,
  PaymentOnboardingShowForPayableQuery,
  PaymentOnboardingStoreRequest,
  PaymentOnboardingWebsiteCandidatesQuery,
  PayoutsDownloadPath,
  PayoutsForPartnerQuery,
  PayoutsForStorePath,
  PayoutsForStoreQuery,
  PayoutsShowPath,
  OrderSummaryResource,
  PayoutResource
} from '~/services/api/generated'

const projectPayout = (payout: PayoutResource, orders?: OrderSummaryResource[]): Payout => {
  const decimal = (value: string, field: string): number =>
    z
      .string()
      .regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/u, {
        error: `Invalid payout projection: ${field} must be a decimal string`
      })
      .transform((decimalString) => Number(decimalString))
      .refine(Number.isFinite, {
        error: `Invalid payout projection: ${field} must be a finite decimal string`
      })
      .parse(value)

  return {
    id: payout.id,
    transaction_id: payout.transaction_id,
    amount: payout.amount,
    currency: payout.currency,
    status: payout.status,
    status_code: payout.status_code,
    payout_details: payout.payout_details,
    store: payout.store,
    included_orders_count: payout.included_orders_count,
    subtotal: payout.subtotal,
    tax_collected: decimal(payout.tax_collected, 'tax_collected'),
    tax_remitted: decimal(payout.tax_remitted, 'tax_remitted'),
    tax_withheld: payout.tax_withheld,
    tip_total: decimal(payout.tip_total, 'tip_total'),
    delivery_fee_total: decimal(payout.delivery_fee_total, 'delivery_fee_total'),
    processing_fee_total: decimal(payout.processing_fee_total, 'processing_fee_total'),
    application_fee_total: decimal(payout.application_fee_total, 'application_fee_total'),
    platform_commission_total: decimal(
      payout.platform_commission_total,
      'platform_commission_total'
    ),
    gross_amount: payout.gross_amount,
    period_start: payout.period_start,
    period_end: payout.period_end,
    destination_account: payout.destination_account,
    destination_name: payout.destination_name,
    destination_entity_type: payout.destination_entity_type,
    ...(orders === undefined ? {} : { orders }),
    initiated_by: payout.initiated_by,
    processed_at: payout.processed_at,
    scheduled_at: payout.scheduled_at,
    created_at: payout.created_at,
    updated_at: payout.updated_at
  }
}

export function usePaymentService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api`
  const onboardingUrl = `${baseURL}/payment-onboarding`
  const client = useApiRequest()

  return {
    async setupPaymentIntent() {
      const response = await client(`${baseURL}/payment-methods/setup-intent`, {
        method: 'POST',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      })
      return PaymentMethodSetupIntentSuccessSchema.parse(response)
    },
    onboarding: () => ({
      async create(body: PaymentOnboardingStoreRequest) {
        const request = PaymentOnboardingStore({}).parse(body)
        const response = await client(onboardingUrl, { method: 'POST', body: request })
        return strictify(PaymentOnboardingStoreSuccessSchema).parse(response)
      },
      async all() {
        const response = await client(`${onboardingUrl}/all`, { method: 'GET' })
        return strictify(PaymentOnboardingAllSuccessSchema).parse(response)
      },
      async list() {
        const response = await client(onboardingUrl, { method: 'GET' })
        return strictify(PaymentOnboardingListSuccessSchema).parse(response)
      },
      async listCompleted() {
        const response = await client(`${onboardingUrl}/completed`, { method: 'GET' })
        return strictify(PaymentOnboardingCompletedSuccessSchema).parse(response)
      },
      availableModels: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingModelsWithoutMerchantPathSchema,
          parse: (model: PaymentOnboardingModelsWithoutMerchantPath['payable']) => ({
            payable: model
          }),
          buildUrl: (path) => `${onboardingUrl}/models-without-merchant/${path.payable}`
        },
        PaymentOnboardingModelsWithoutMerchantSuccessSchema,
        'GET'
      ),
      show: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingShowPathSchema,
          parse: (id: number) => ({ onboarding: id }),
          buildUrl: (path) => `${onboardingUrl}/${path.onboarding}`
        },
        PaymentOnboardingShowSuccessSchema,
        'GET'
      ),
      sync: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingSyncPathSchema,
          parse: (id: number) => ({ onboarding: id }),
          buildUrl: (path) => `${onboardingUrl}/${path.onboarding}/sync`
        },
        PaymentOnboardingSyncSuccessSchema,
        'GET'
      ),
      destroy: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingDestroyPathSchema,
          parse: (id: number) => ({ onboarding: id }),
          buildUrl: (path) => `${onboardingUrl}/${path.onboarding}`
        },
        PaymentOnboardingDestroySuccessSchema,
        'DELETE'
      ),
      changeBusinessType: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingChangeBusinessTypePathSchema,
          parse: (id: number, _body: PaymentOnboardingChangeBusinessTypeRequest) => ({
            onboarding: id
          }),
          buildUrl: (path) => `${onboardingUrl}/${path.onboarding}/business-type`
        },
        PaymentOnboardingChangeBusinessTypeSuccessSchema,
        'PATCH',
        {
          channel: 'body',
          schema: PaymentOnboardingChangeBusinessTypeRequestSchema.strict(),
          select: (_id, body) => body
        }
      ),
      createSession: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingSessionPathSchema,
          parse: (id: number, _body: PaymentOnboardingSessionRequest = {}) => ({ onboarding: id }),
          buildUrl: (path) => `${onboardingUrl}/${path.onboarding}/session`
        },
        PaymentOnboardingSessionSuccessSchema,
        'POST',
        { channel: 'body', schema: PaymentOnboardingSession({}), select: (_id, body = {}) => body }
      ),
      async assignChild(parentId: number, childId: number) {
        const path = PaymentOnboardingAssignParentPathSchema.strict().parse({
          child: childId,
          parent: parentId
        })
        const response = await client(`${onboardingUrl}/${path.parent}/assign/${path.child}`, {
          method: 'POST'
        })
        return strictify(PaymentOnboardingAssignParentSuccessSchema).parse(response)
      },
      createChild: createGeneratedResourceOperation(
        client,
        {
          schema: PaymentOnboardingCreateChildPathSchema,
          parse: (parentId: number, _body: PaymentOnboardingCreateChildRequest) => ({
            parent: parentId
          }),
          buildUrl: (path) => `${onboardingUrl}/${path.parent}/create-child`
        },
        PaymentOnboardingCreateChildSuccessSchema,
        'POST',
        { channel: 'body', schema: PaymentOnboardingCreateChild({}), select: (_id, body) => body }
      ),
      websiteCandidates: createGeneratedResourceOperation(
        client,
        `${onboardingUrl}/website-candidates`,
        PaymentOnboardingWebsiteCandidatesSuccessSchema,
        'GET',
        {
          channel: 'query',
          schema: PaymentOnboardingWebsiteCandidatesQuerySchema.strict(),
          select: (query: PaymentOnboardingWebsiteCandidatesQuery) => query
        }
      ),
      showForPayable: createGeneratedResourceOperation(
        client,
        `${onboardingUrl}/for-payable`,
        PaymentOnboardingShowForPayableSuccessSchema,
        'GET',
        {
          channel: 'query',
          schema: PaymentOnboardingShowForPayableQuerySchema.strict(),
          select: (query: PaymentOnboardingShowForPayableQuery) => query
        }
      )
    }),
    paymentMethod: () => ({
      async save(setupIntentId: PaymentMethodSaveRequest['setup_intent_id']) {
        const body = PaymentMethodSave({}).parse({
          setup_intent_id: setupIntentId
        })
        const response = await client(`${baseURL}/payment-methods`, {
          method: 'POST',
          body
        })
        // PaymentMethodSaveSuccess.data is a bare z.union([Link, Card]) with Link listed
        // first: a plain, non-strict parse lets a full card payload match the narrower
        // Link shape and silently drop brand/last4/expiry. strictify forces each union
        // member to reject the other's fields, restoring correct discrimination.
        const success = strictify(PaymentMethodSaveSuccessSchema).parse(response)
        return { data: success.data }
      },
      async list() {
        const response = await client(`${baseURL}/payment-methods`, { method: 'GET' })
        const success = strictify(PaymentMethodListSuccessSchema).parse(response)
        return { data: success.data }
      },
      async setDefault(paymentMethod: number) {
        const path = PaymentMethodSetDefaultPathSchema.strict().parse({ paymentMethod })
        const response = await client(`${baseURL}/payment-methods/${path.paymentMethod}/default`, {
          method: 'POST'
        })
        const success = strictify(PaymentMethodSetDefaultSuccessSchema).parse(response)
        return { data: success.data }
      },
      async remove(paymentMethod: number): Promise<void> {
        const path = PaymentMethodDeletePathSchema.strict().parse({ paymentMethod })
        const response = await client(`${baseURL}/payment-methods/${path.paymentMethod}`, {
          method: 'DELETE'
        })
        PaymentMethodDeleteSuccessSchema.parse(response)
      }
    }),
    payout: () => ({
      async show(payoutId: PayoutsShowPath['payout']) {
        const path = PayoutsShowPathSchema.strict().parse({ payout: payoutId })
        const success = strictify(PayoutsShowSuccessSchema).parse(
          await client(`${baseURL}/payouts/${path.payout}`, { globalErrorUi: true, method: 'GET' })
        )
        return { data: projectPayout(success.data, success.data.orders) }
      },
      async listForPartner(filters: PayoutsForPartnerQuery = {}) {
        const query = PayoutsForPartnerQuerySchema.strict().parse(filters)
        const response = await client(`${baseURL}/payouts/for-partner`, {
          method: 'GET',
          query
        })
        const success = strictify(PayoutsForPartnerSuccessSchema).parse(response)
        return { ...success, data: success.data.map((payout) => projectPayout(payout)) }
      },
      async listForStore(
        storeId: PayoutsForStorePath['store'],
        filters: PayoutsForStoreQuery = {}
      ) {
        const path = PayoutsForStorePathSchema.strict().parse({ store: storeId })
        const query = PayoutsForStoreQuerySchema.strict().parse(filters)
        const response = await client(`${baseURL}/payouts/for-store/${path.store}`, {
          method: 'GET',
          query
        })
        const success = strictify(PayoutsForStoreSuccessSchema).parse(response)
        return { ...success, data: success.data.map((payout) => projectPayout(payout)) }
      },
      async download(payoutId: PayoutsDownloadPath['payout']): Promise<Blob> {
        const path = PayoutsDownloadPathSchema.strict().parse({ payout: payoutId })
        const response = await client<Blob, 'blob'>(`${baseURL}/payouts/${path.payout}/download`, {
          method: 'GET',
          responseType: 'blob'
        })
        return pdfBlobSchema.parse(response)
      }
    })
  }
}
